import { QueryClient } from "@tanstack/react-query";
import axios from "axios";

/**
 * Errors that will never succeed on a retry. 401 in particular is already
 * handled by the response interceptor in `src/lib/apiClient.ts`, which refreshes
 * the token and replays the request once — retrying on top of that would
 * multiply token refreshes for a single failure.
 */
const isTerminalError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) return false;
  const status = error.response?.status;
  return status === 401 || status === 403 || status === 404;
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Long enough that moving between the location tabs never refetches;
      // short enough that data still feels live. Slow-moving data (pricing,
      // available countries/types) overrides this with a much longer value.
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      // This is an operator dashboard that sits open in a background tab for
      // long stretches; refetching every time it regains focus would surprise
      // users mid-task and undo the point of the cache.
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => !isTerminalError(error) && failureCount < 2,
    },
    mutations: {
      retry: false,
    },
  },
});
